import { classes } from "polytype";
import {
  type AuthenticateMfaRequestData,
  SessionToken,
} from "../../auth/index.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { language } from "../../base/i18n/setup.js";
import {
  AggregateMetaData,
  DataModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import type { Salutation } from "../../customer/index.js";
import type { DomFile, FileAccessTokenProvider } from "../../file/index.js";
import { File } from "../../file/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import type {
  UserAuthenticateRequestData,
  UserConfirmPasswordResetRequestData,
  UserData,
  UserDeleteRequestData,
  UserRegisterRequestData,
  UserResendVerificationEmailRequestData,
  UserUpdatePasswordRequestData,
  UserUpdatePersonalInformationRequestData,
  UserVerifyEmailRequestData,
  UserVerifyPhoneNumberRequestData,
  UserVerifyRegistrationRequestData,
} from "./types.js";
import { UserAvatarAccessTokenProvider } from "./UserAvatarAccessTokenProvider.js";

export class User extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("user", "user");
  public static self = User.ofId("self");
  public readonly fileAccessTokenProvider: FileAccessTokenProvider;

  public constructor(id: string) {
    super(id);
    this.fileAccessTokenProvider = new UserAvatarAccessTokenProvider(this);
  }

  public static findAggregate(userId?: string) {
    return userId ? { id: userId, ...User.aggregateMetaData } : undefined;
  }

  public static ofId(id: string) {
    return new User(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.user.find(id);

    if (data !== undefined) {
      return new UserDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const user = await this.find(id);
    assertObjectFound(user, "user", id);
    return user;
  });

  public static own = provideReact(async () => {
    const user = await this.find("self");
    assertObjectFound(user, "user", "self");
    return user;
  });

  public findDetailed = provideReact(
    () => User.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<UserDetailed | undefined>>;

  public getDetailed = provideReact(
    () => User.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<UserDetailed>>;

  public async uploadAvatar(file: DomFile) {
    await File.upload(file, this.fileAccessTokenProvider);
  }

  public getAvatarUploadRules = provideReact(async () => {
    return File.getUploadRules("avatar");
  });

  public getPasswordUpdatedAt = provideReact(async () => {
    return await config.behaviors.user.getPasswordUpdatedAt();
  });

  public async updatePersonalInformation(
    data: UserUpdatePersonalInformationRequestData,
  ) {
    await config.behaviors.user.updatePersonalInformation(this.id, data);
  }

  public async addPhoneNumber(phoneNumber: string) {
    await config.behaviors.user.addPhoneNumber(this.id, phoneNumber);
  }

  public async verifyPhoneNumber(data: UserVerifyPhoneNumberRequestData) {
    const response = await config.behaviors.user.verifyPhoneNumber(
      this.id,
      data,
    );

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path === "verification_token" ? "code" : e.path,
      }));
    }
  }

  public async removePhoneNumber() {
    await config.behaviors.user.removePhoneNumber(this.id);
  }

  public async updateEmail(email: string) {
    await config.behaviors.user.updateEmail(email);
  }

  public async verifyEmail(data: UserVerifyEmailRequestData) {
    await config.behaviors.user.verifyEmail(data);
  }

  public async resendVerificationEmail(email: string) {
    return await config.behaviors.user.resendVerificationEmail({
      userId: this.id,
      email,
    });
  }

  public async removeAvatar() {
    await config.behaviors.user.removeAvatar(this.id);
  }

  public async updatePassword(data: UserUpdatePasswordRequestData) {
    return await config.behaviors.user.updatePassword(data);
  }

  public static async resetPassword(email: string) {
    await config.behaviors.user.resetPassword(email);
  }

  public static async confirmPasswordReset(
    data: UserConfirmPasswordResetRequestData,
  ) {
    await config.behaviors.user.confirmPasswordReset(data);
  }

  public static async login(data: UserAuthenticateRequestData) {
    const result = await config.behaviors.user.authenticate(data);
    if (result === "mfaRequired") {
      return result;
    }

    return new SessionToken(result);
  }

  public static async authenticateMfa(data: AuthenticateMfaRequestData) {
    await config.behaviors.mfa.authenticateMfa(data);
  }

  public static async register(data: UserRegisterRequestData) {
    return await config.behaviors.user.register(data);
  }

  public static async verifyRegistration(
    data: UserVerifyRegistrationRequestData,
  ) {
    return await config.behaviors.user.verifyRegistration(data);
  }

  public static async resendVerificationEmail(
    data: UserResendVerificationEmailRequestData,
  ) {
    return await config.behaviors.user.resendVerificationEmail(data);
  }

  public async delete(data: UserDeleteRequestData) {
    return await config.behaviors.user.delete(data);
  }

  public async logout() {
    await config.behaviors.user.logout();
  }
}

class UserCommon extends classes(DataModel<UserData>, User) {
  public readonly firstName: string;
  public readonly lastName: string;
  public readonly fullName: string;
  public readonly avatar?: File;
  public readonly email?: string;
  public readonly title?: Salutation;
  public readonly translatedTitle?: string;
  public readonly phoneNumber?: string;

  public constructor(data: UserData) {
    super([data], [data.userId]);
    this.firstName = data.person.firstName;
    this.lastName = data.person.lastName;
    this.fullName = `${data.person.firstName} ${data.person.lastName}`;
    this.avatar = data.avatarRef ? File.ofId(data.avatarRef) : undefined;
    this.email = data.email;
    this.title = data.person.title;
    this.translatedTitle =
      this.title === "mr" || this.title === "ms"
        ? language.default.user.title[this.title]
        : undefined;
    this.phoneNumber = data.phoneNumber;
  }
}

export class UserDetailed extends classes(UserCommon, DataModel<UserData>) {
  public constructor(data: UserData) {
    super([data], [data]);
  }
}
